import { Link as GatsbyLink, GatsbyLinkProps, navigate as gatsbyNavigate } from "gatsby"
import React, { AnchorHTMLAttributes, forwardRef } from "react"

import { NavigateOptions } from "@reach/router"
import { getCookie } from "./cookie"
import i18next from "../../i18n/config"
import { shared } from "../../config/shared"
import { useLocalesState } from "../context/locales"

const buildPath = (language: string, region: string, path: string) => {
    const splittedPath = path.split("?")
    if (process.env.DEFAULT_PATH) return path
    if (
        ["/imprint", "/privacy", "/terms", "/revocation-policy", "/accessibility", "/cancel-contract"].some(a =>
            splittedPath[0].includes(a)
        )
    )
        return path
    let uri = splittedPath[0].split("/")
    const search = splittedPath[1]
    if (process.env.IGNORE_REGION_IN_PATH ? /^\w{2}$/.test(uri[1]) : /^\w{2}-\w{2}$/.test(uri[1])) {
        uri.splice(
            1,
            1,
            language === process.env.DEFAULT_LANGUAGE
                ? ""
                : language + (!process.env.IGNORE_REGION_IN_PATH ? `-${region.toLowerCase()}` : "")
        )
    } else {
        uri.splice(
            1,
            0,
            language === process.env.DEFAULT_LANGUAGE
                ? ""
                : language + (!process.env.IGNORE_REGION_IN_PATH ? `-${region.toLowerCase()}` : "")
        )
    }
    if (uri[1] === "") {
        uri.splice(1, 1)
    }
    if (search) uri.push(`?${search}`)
    return uri.join("/") || "/"
}

const navigate = (path: string, options?: NavigateOptions<{}>) => {
    const language = i18next.language
    const region = getCookie("__region__") || process.env.REGION
    return gatsbyNavigate(buildPath(language.split(/-|_/)[0], region, path), options)
}

type GatsbyLinkWrapperProps = Omit<GatsbyLinkProps<any>, "ref">

const Link = forwardRef<HTMLAnchorElement, GatsbyLinkWrapperProps>(({ children, to, ...rest }, ref) => {
    const { language, region } = useLocalesState()

    if (!to) return null

    return (
        <GatsbyLink {...rest} to={buildPath(language.split(/-|_/)[0], region, to)} ref={ref as React.Ref<any>}>
            {children}
        </GatsbyLink>
    )
})

Link.displayName = "Link"

type AProps = AnchorHTMLAttributes<HTMLAnchorElement> & {
    noURLReplace?: boolean
    state?: GatsbyLinkProps<any>["state"]
}

const A = forwardRef<HTMLAnchorElement, AProps>(({ href, children, noURLReplace = false, state, ...rest }, ref) => {
    if (!href) return null

    try {
        const url = new URL(href)
        const path = url.pathname
        const origin = url.origin

        if (origin === shared.router.url || origin === shared.router.menuUrl) {
            return (
                <Link {...rest} state={state} to={path + url.search + url.hash} ref={ref}>
                    {children}
                </Link>
            )
        } else if (
            !noURLReplace &&
            (origin === process.env.REPLACE_MENU_PART_SHOP ||
                origin === process.env.REPLACE_MENU_PART_WEB ||
                origin === process.env.REPLACE_MENU_PART_ACCOUNT)
        ) {
            return (
                <a
                    {...rest}
                    ref={ref}
                    href={href
                        .replace(process.env.REPLACE_MENU_PART_WEB, process.env.WEB_URL)
                        .replace(process.env.REPLACE_MENU_PART_SHOP, process.env.SHOP_URL)
                        .replace(process.env.REPLACE_MENU_PART_ACCOUNT, process.env.ACCOUNT_URL)}
                >
                    {children}
                </a>
            )
        } else {
            return (
                <a {...rest} ref={ref} href={href}>
                    {children}
                </a>
            )
        }
    } catch {
        return null
    }
})

A.displayName = "A"

export { navigate, Link, A, buildPath }
